/**
 * Chat widget orchestrator.
 *
 * Responsibilities (everything else lives in sibling files):
 *   - apply defaults / validate options (`./config`)
 *   - manage session id (`./session`)
 *   - build DOM (`./dom`)
 *   - inject CSS (`./dom`)
 *
 * What stays here:
 *   - the open/close state machine
 *   - wiring DOM events to the NexorClient.chat() call
 *   - lead-capture submission flow
 *   - the public ChatHandle returned to callers
 */
import type { NexorClient, PowSolution } from "../client";
import type { ChatMedia, UUID } from "../types";
import { solvePow } from "../pow";
import {
  applyDefaults,
  needsCapture,
  type ChatInitOptions,
  type ChatHandle,
  type ResolvedCfg,
  type ContactRequestChannel,
} from "./config";
import {
  ensureSessionId,
  touchSession,
  SESSION_RETENTION_MS,
} from "./session";
import { isValidEmail } from "./validate";
import { renderLeadTemplate } from "./template";
import { countryByIso, maxNsn, resolveDefaultCountry } from "./countries";
import {
  buildLauncher,
  buildMessage,
  buildMediaMessage,
  buildPanel,
  buildResumeBanner,
  buildTypingIndicator,
  formatRelativeTime,
  injectStyles,
  type LauncherRefs,
  type MessageRole,
  type PanelRefs,
} from "./dom";

// Re-export the public types so consumers can `import type { ChatHandle } from "@getnexorai/sdk"`.
export type {
  ChatInitOptions,
  ChatHandle,
  ChatLeadCapture,
  ChatChannels,
  ChatChannelKey,
} from "./config";

// ─── Iframe embedding detection ─────────────────────────────────────────────

function detectEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

// The real parent-page origin. `ancestorOrigins` comes straight from the
// browser and can't be spoofed by the embedding page's JS; `document.referrer`
// is the fallback for browsers that don't expose it.
function detectParentOrigin(): string | undefined {
  const ancestors = window.location.ancestorOrigins;
  if (ancestors && ancestors.length) return ancestors[0];
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/**
 * Mount the chat widget and return a handle for programmatic control.
 *
 * @param client  The NexorClient instance that will send turns.
 * @param options User-facing widget configuration. See ChatInitOptions.
 * @throws If called outside a browser, or without a workflowId.
 */
export function initChat(
  client: NexorClient,
  options: ChatInitOptions,
): ChatHandle {
  ensureBrowser();
  if (!options || !options.workflowId) {
    throw new Error("Nexor SDK: initChat() requires `workflowId`.");
  }

  const cfg = applyDefaults(options);
  injectStyles(cfg.accentColor, cfg.accentTextColor);

  // Iframe-embedded widgets (chat.getnexor.ai) send origin-bound turns via
  // widgetTurn() instead of chat() — see flush() below.
  const isEmbedded = cfg.embedded ?? detectEmbedded();
  const parentOrigin = isEmbedded ? detectParentOrigin() : undefined;

  // Mutable: "start over" mints a fresh session id (and history key) without
  // tearing down the widget.
  let sessionId = ensureSessionId();
  let historyKey = `nexor.chat.history.${sessionId}`;
  const root = buildRoot(cfg);
  const launcher = buildLauncher(cfg);
  const panel = buildPanel(cfg);
  root.appendChild(launcher.wrap);
  root.appendChild(panel.el);
  (cfg.container ?? document.body).appendChild(root);

  const hasFan = launcher.channelButtons.length > 0;

  // ─── State (closure-private — no external observers needed) ─────────────
  const state = {
    isOpen: false,
    expanded: false, // speed-dial fan visible (touch/click-pinned)
    unread: 0,
    leadId: undefined as UUID | undefined,
    captured: !needsCapture(cfg),
    pendingLead: { ...(cfg.lead ?? {}) },
    history: [] as Array<{ role: "user" | "bot"; text: string; ts?: number; media?: ChatMedia }>,
    // Dashboard-authored web-chat config (fetched once on init). Until it
    // resolves, assume enabled and fall back to the host-provided `greeting`.
    webchatEnabled: true,
    serverInitialMessage: null as string | null,
    configResolved: false,
    disabledShown: false,
    // The opening message actually displayed to the visitor — sent to the API
    // so the agent's <webchat_opening> matches what was shown (esp. for a
    // per-page openingMessage override).
    shownOpening: null as string | null,
    // Set by "start over" → the next turn tells the API to reset the conversation
    // episode (keep the lead, clear its prior run/thread/variables). Cleared once
    // that turn lands.
    resetEpisode: false,
    // True when this page load started with NO restored transcript. Used to
    // decide whether to offer the "continue / start fresh" choice when the API
    // reports it recognised a returning lead (matched_existing).
    freshLocalSession: false,
    // Ensures the returning-lead choice banner is offered at most once.
    episodeChoiceOffered: false,
    // The channel a visitor picked in the contact-request form (call/email/...).
    contactChannel: null as ContactRequestChannel | null,
    // In-flight "create lead on form submit" request — the first chat turn waits
    // on it so the backend matches that lead (no duplicate) rather than racing it.
    leadCreating: null as Promise<void> | null,
  };

  // Resolves once GET /chat/config returns (or fails). The greeting waits on
  // this so it can use the dashboard `initial_message` rather than the fallback.
  let configReady: Promise<void>;

  // Composer/send state, hoisted above DOM wiring + history restore so the
  // call chain `restore → appendBubble → appendNode → send.typing` (and the
  // openOnLoad → appendBot greeting path) doesn't hit a TDZ. See the
  // debounced/coalesced send logic further down for how this is used.
  const send = {
    pending: [] as string[],
    timer: 0,
    inFlight: false,
    typing: null as HTMLElement | null,
    splitTimer: 0, // staggers multi-bubble bot replies
  };

  // First-impression delay: the lazy-greet bubble used to appear instantly,
  // which read as canned. Show typing dots for ~2s first so the greeting feels
  // composed in the moment. Cancelled if the visitor sends before it fires.
  const greeting = { pending: false, timer: 0 };
  const GREETING_DELAY_MS = 2000;

  // ─── DOM event wiring ───────────────────────────────────────────────────
  // Without a fan the bubble simply opens/closes the chat. With a fan, the
  // bubble toggles the channel menu (desktop also reveals it on hover via CSS);
  // the "chat" button inside the fan is what actually opens the panel.
  launcher.el.addEventListener("click", hasFan ? toggleFan : toggle);
  panel.closeBtn.addEventListener("click", close);
  panel.restartBtn?.addEventListener("click", startOver);
  panel.form.addEventListener("submit", onComposerSubmit);
  // Composer textarea: grow with content, and Enter sends / Shift+Enter newline.
  panel.input.addEventListener("input", autosizeComposer);
  panel.input.addEventListener("keydown", (e: KeyboardEvent) => {
    // `isComposing` guards IME (e.g. accents, CJK) — don't send mid-composition.
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      onComposerSubmit(e);
    }
  });
  panel.captureForm?.addEventListener("submit", onCaptureSubmit);
  // Clear the inline validation error as soon as the visitor edits any field.
  for (const input of Object.values(panel.captureInputs)) {
    input?.addEventListener("input", clearCaptureError);
  }
  // Phone field (chat capture): digits only, capped to the selected country's
  // number length; re-cap (and trim any excess) when the country changes.
  if (panel.phoneCountry && panel.captureInputs.phone) {
    wirePhoneCap(panel.phoneCountry, panel.captureInputs.phone, clearCaptureError);
  }

  // ─── Contact-request form wiring (call me / email me) ────────────────────
  if (panel.contact) {
    const c = panel.contact;
    c.form.addEventListener("submit", onContactSubmit);
    for (const input of Object.values(c.inputs)) {
      input?.addEventListener("input", clearContactError);
    }
    if (c.phoneCountry && c.inputs.phone) {
      wirePhoneCap(c.phoneCountry, c.inputs.phone, clearContactError);
    }
    // "Done" (success) dismisses the form; close() restores the base view so a
    // re-open shows the normal chat/capture screen, not a stale success panel.
    c.successBtn.addEventListener("click", close);
  }

  // Wire the fan buttons: the chat action opens the panel; channel links just
  // collapse the fan once tapped (they navigate natively).
  let onDocPointerDown: ((e: Event) => void) | null = null;
  if (hasFan) {
    for (const btn of launcher.channelButtons) {
      btn.el.addEventListener("click", () => {
        if (btn.key === "chat") {
          collapseFan();
          open();
        } else if (btn.action === "contact") {
          collapseFan();
          openContact(btn.key as ContactRequestChannel);
        } else {
          collapseFan(); // link navigates on its own
        }
      });
    }
    // Tap/click anywhere outside the launcher collapses the fan (touch devices
    // have no hover-out to rely on).
    onDocPointerDown = (e: Event) => {
      if (state.expanded && !launcher.wrap.contains(e.target as Node)) collapseFan();
    };
    document.addEventListener("pointerdown", onDocPointerDown);
  }

  // Track the resume banner so "start over" can remove it.
  let resumeBanner: HTMLElement | null = null;

  // Restore a prior transcript (persisted up to SESSION_RETENTION_MS) so a
  // page reload keeps the conversation. A returning visitor is ALREADY past the
  // capture form — re-showing it on top of an existing conversation (the broken
  // state) makes no sense. So when there's history we: keep their known info,
  // mark them captured, hide the form, and show a "continue or start over"
  // banner above the messages.
  const persisted = loadPersisted();
  // No restored transcript → a "returning lead" can only be discovered from the
  // API (matched_existing), so the choice banner is offered then. With restored
  // history we already show the resume banner below, so skip the API-driven one.
  state.freshLocalSession = persisted.history.length === 0;
  if (persisted.history.length) {
    state.history = persisted.history;
    if (persisted.lead) state.pendingLead = { ...state.pendingLead, ...persisted.lead };
    state.captured = true;
    setView("chat");
    renderResumeBanner();
    for (const m of persisted.history) {
      if (m.media) appendNode(buildMediaMessage(m.media));
      else appendBubble(m.role === "user" ? "user" : "bot", m.text, m.ts);
    }
    scrollToBottom();
  }

  // Lock/unlock the composer to match the (possibly just-restored) captured state.
  syncComposerLock();

  // Fetch the dashboard-authored web-chat config (enabled flag + opening
  // message) once. Fire-and-forget; the greeting awaits `configReady`.
  configReady = loadServerConfig();

  if (cfg.openOnLoad) open();

  // ─── State machine ──────────────────────────────────────────────────────

  // ─── Speed-dial fan (channels) ──────────────────────────────────────────

  function toggleFan(): void {
    state.expanded ? collapseFan() : expandFan();
  }
  function expandFan(): void {
    state.expanded = true;
    launcher.wrap.setAttribute("data-expanded", "true");
    launcher.el.setAttribute("aria-expanded", "true");
  }
  function collapseFan(): void {
    if (!state.expanded) return;
    state.expanded = false;
    launcher.wrap.setAttribute("data-expanded", "false");
    launcher.el.setAttribute("aria-expanded", "false");
  }

  function open(): void {
    collapseFan(); // the panel takes over; no need for the fan underneath
    if (state.isOpen) return;
    state.isOpen = true;
    root.setAttribute("data-open", "true");
    clearUnread();
    // Web chat turned off in the dashboard → no capture, no greeting, just a notice.
    if (!state.webchatEnabled) {
      showDisabled();
      cfg.onOpen?.();
      return;
    }
    // Lazy-greet: first time the panel opens, drop the greeting message.
    if (state.history.length === 0 && state.captured && canGreet()) {
      scheduleGreeting();
    }
    // Focus the first thing the visitor should fill: the capture form when it's
    // gating chat, otherwise the composer.
    if (!state.captured) {
      const first = cfg.capture.fields[0];
      ((first && panel.captureInputs[first]) || panel.input).focus();
    } else {
      panel.input.focus();
    }
    cfg.onOpen?.();
  }

  function close(): void {
    if (!state.isOpen) return;
    state.isOpen = false;
    root.setAttribute("data-open", "false");
    // Leaving the contact-request screen → restore the normal view AND the chat
    // header, so a re-open shows chat/capture (not the form or a stale success).
    if (panel.el.getAttribute("data-view") === "contact") {
      state.contactChannel = null;
      setView(baseView());
      restoreHeader();
    }
    cfg.onClose?.();
  }

  /** Restore the chat title/subtitle in the header after the contact form. */
  function restoreHeader(): void {
    panel.headerTitle.textContent = cfg.title;
    panel.headerSubtitleText.textContent = cfg.subtitle ?? "";
    panel.headerSubtitle.hidden = !cfg.subtitle;
  }

  function toggle(): void {
    state.isOpen ? close() : open();
  }

  // ─── Capture flow ───────────────────────────────────────────────────────

  function onCaptureSubmit(e: Event): void {
    e.preventDefault();
    clearCaptureError();
    const collected = readCaptureFields(panel);
    const required = cfg.capture.fields;
    const msgs = cfg.capture.errorMessages;

    // 1) Every required field must be filled.
    for (const f of required) {
      if (!collected[f]) return failCapture(f, msgs.required);
    }
    // 2) Email must look like an email.
    if (required.includes("email") && !isValidEmail(collected.email ?? "")) {
      return failCapture("email", msgs.email);
    }
    // 3) Phone: the national digit count must match the selected country, then
    //    store the full E.164 (dial + digits) so the backend gets a clean number.
    if (required.includes("phone")) {
      const country = countryByIso(panel.phoneCountry?.value);
      const digits = (collected.phone ?? "").replace(/\D/g, "");
      if (!country || digits.length < country.nsn[0] || digits.length > country.nsn[1]) {
        return failCapture("phone", msgs.phone);
      }
      collected.phone = `${country.dial}${digits}`;
    }

    state.pendingLead = { ...state.pendingLead, ...collected };
    state.captured = true;
    // Create the lead in Nexor now (opt-in) — don't wait for the first message.
    persistCapturedLead();
    setView("chat"); // leave the capture screen, reveal the conversation
    syncComposerLock();
    panel.input.focus();
    // Greet once capture clears, so the greeting feels like a response to "I'm ready".
    if (state.history.length === 0 && canGreet()) scheduleGreeting();
  }

  /**
   * Create the lead in Nexor the instant the capture form is submitted (opt-in
   * via capture.createLeadOnSubmit), so a visitor who fills the form but never
   * sends a message is still captured. Fire-and-forget: the chat flow never waits
   * on it and a failure never blocks chatting. The first chat turn then MATCHES
   * this lead by email/phone, so there's no duplicate.
   */
  function persistCapturedLead(): void {
    if (!cfg.capture.createLeadOnSubmit || state.leadId) return;
    const lead = state.pendingLead;
    if (!lead.first_name && !lead.email && !lead.phone) return;
    const resolvedMeta =
      typeof cfg.metadata === "function" ? cfg.metadata() : cfg.metadata;
    state.leadCreating = client
      .createLead({
        first_name: lead.first_name || "Web visitor",
        last_name: lead.last_name,
        email: lead.email,
        phone: lead.phone,
        source: "web-chat",
        metadata: {
          ...(resolvedMeta as Record<string, unknown> | undefined),
          web_chat_session_id: sessionId,
        },
      })
      .then((res) => {
        if (res?.lead?.id && !state.leadId) {
          state.leadId = res.lead.id;
          cfg.onLeadCaptured?.(res.lead.id);
        }
        // A brand-new lead isn't a "returning visitor": suppress the
        // matched_existing resume banner the first chat turn would otherwise
        // raise (that turn WILL match this just-created lead by identity).
        if (res && !res.existed) state.episodeChoiceOffered = true;
      })
      .catch((err) => {
        // Non-blocking: the first chat turn still creates/matches the lead.
        cfg.onError?.(err as Error);
      })
      .finally(() => {
        state.leadCreating = null;
      });
  }

  // ─── Capture validation feedback ────────────────────────────────────────

  function failCapture(field: keyof typeof panel.captureInputs, message: string): void {
    showCaptureError(message);
    const input = panel.captureInputs[field];
    // For phone, outline the whole composite (flag + input), not just the input.
    const target = (input?.closest(".nexor-chat__phone") as HTMLElement | null) || input;
    target?.classList.add("nexor-chat__input--error");
    input?.focus();
  }
  function showCaptureError(message: string): void {
    if (!panel.captureError) return;
    panel.captureError.textContent = message;
    panel.captureError.hidden = false;
  }
  function clearCaptureError(): void {
    if (panel.captureError) {
      panel.captureError.textContent = "";
      panel.captureError.hidden = true;
    }
    // Covers both plain inputs and the phone composite wrapper.
    panel.captureForm
      ?.querySelectorAll(".nexor-chat__input--error")
      .forEach((el) => el.classList.remove("nexor-chat__input--error"));
  }

  // ─── Contact-request flow (call me / email me) ──────────────────────────

  /** Cap a phone input to its country's digit length + strip non-digits live.
   *  Shared by the chat-capture and contact-request phone fields. */
  function wirePhoneCap(
    select: HTMLSelectElement,
    input: HTMLInputElement,
    onChange?: () => void,
  ): void {
    const cap = (): void => {
      const c = countryByIso(select.value);
      const max = c ? maxNsn(c) : 15;
      input.maxLength = max;
      const digits = input.value.replace(/\D/g, "").slice(0, max);
      if (input.value !== digits) input.value = digits;
    };
    input.addEventListener("input", cap);
    select.addEventListener("change", () => {
      cap();
      onChange?.();
    });
  }

  /** Open the panel straight into the contact-request form for `channel`. */
  function openContact(channel: ContactRequestChannel): void {
    const c = panel.contact;
    const rc = cfg.requestContact;
    if (!c || !rc) return;
    state.contactChannel = channel;

    // Re-contextualize the panel HEADER as the form's title + explainer, so the
    // black bar itself reads as a form (the chat "we reply in minutes" subtitle
    // and the live-status dot — hidden via CSS in this view — make no sense here).
    panel.headerTitle.textContent = rc.title[channel] ?? cfg.title;
    panel.headerSubtitleText.textContent = rc.subtitle[channel] ?? "";
    panel.headerSubtitle.hidden = !rc.subtitle[channel];
    c.submitBtn.textContent = rc.submitLabel[channel] ?? "";
    c.successText.textContent = rc.successText[channel] ?? "";

    // Reset to a clean form state (clear any prior success / errors / values).
    c.view.setAttribute("data-state", "form");
    c.submitBtn.disabled = false;
    clearContactError();
    resetContactForm();

    // Open the panel into the contact view — skip the greeting/capture logic.
    cancelGreeting();
    state.isOpen = true;
    root.setAttribute("data-open", "true");
    clearUnread();
    setView("contact");
    // Focus the first field still needing input (skip any we pre-filled).
    const firstEmpty = [c.inputs.first_name, c.inputs.email, c.inputs.phone].find(
      (i) => i && !i.value,
    );
    (firstEmpty ?? c.inputs.first_name ?? null)?.focus();
    cfg.onOpen?.();
  }

  /**
   * Reset the contact form to the freshest known lead before each open: clears
   * any stale data a visitor typed but didn't submit, and pre-fills whatever we
   * already know (e.g. from a chat capture earlier this session) so they don't
   * retype it. Mirrors buildPhoneField's national-number prefill.
   */
  function resetContactForm(): void {
    const c = panel.contact;
    if (!c) return;
    const lead = state.pendingLead;
    if (c.inputs.first_name) c.inputs.first_name.value = lead.first_name ?? "";
    if (c.inputs.last_name) c.inputs.last_name.value = lead.last_name ?? "";
    if (c.inputs.email) c.inputs.email.value = lead.email ?? "";
    const def = resolveDefaultCountry(cfg.capture.phoneCountryCode);
    if (c.phoneCountry) c.phoneCountry.value = def.iso;
    if (c.inputs.phone) {
      let national = "";
      if (lead.phone) {
        const digits = String(lead.phone).replace(/\D/g, "");
        const dialDigits = def.dial.replace(/\D/g, "");
        national = (digits.startsWith(dialDigits) ? digits.slice(dialDigits.length) : digits).slice(
          0,
          maxNsn(def),
        );
      }
      c.inputs.phone.maxLength = maxNsn(def);
      c.inputs.phone.value = national;
    }
  }

  function onContactSubmit(e: Event): void {
    e.preventDefault();
    const c = panel.contact;
    const channel = state.contactChannel;
    if (!c || !channel) return;
    clearContactError();
    const msgs = cfg.capture.errorMessages;

    const first = c.inputs.first_name?.value.trim() ?? "";
    const last = c.inputs.last_name?.value.trim() ?? "";
    const email = c.inputs.email?.value.trim() ?? "";
    const phoneDigits = (c.inputs.phone?.value ?? "").replace(/\D/g, "");

    // First name is always required.
    if (!first) return failContact("first_name", msgs.required);

    // Email: required for the email channel; whenever provided, must be valid.
    const needEmail = channel === "email";
    if (needEmail && !email) return failContact("email", msgs.required);
    if (email && !isValidEmail(email)) return failContact("email", msgs.email);

    // Phone: required for call/whatsapp; whenever provided, must fit its country.
    const needPhone = channel === "call" || channel === "whatsapp";
    let e164: string | undefined;
    if (needPhone || phoneDigits) {
      const country = countryByIso(c.phoneCountry?.value);
      if (!country || phoneDigits.length < country.nsn[0] || phoneDigits.length > country.nsn[1]) {
        return failContact("phone", msgs.phone);
      }
      e164 = `${country.dial}${phoneDigits}`;
    }

    void submitContact(channel, { first, last, email, phone: e164 });
  }

  async function submitContact(
    channel: ContactRequestChannel,
    data: { first: string; last: string; email: string; phone?: string },
  ): Promise<void> {
    const c = panel.contact;
    const rc = cfg.requestContact;
    if (!c || !rc) return;

    c.submitBtn.disabled = true;
    const resolvedMeta =
      typeof cfg.metadata === "function" ? cfg.metadata() : cfg.metadata;
    const meta = (resolvedMeta as Record<string, unknown> | undefined) ?? undefined;
    const pageUrl =
      (meta && meta.page_url) ||
      (typeof window !== "undefined" ? window.location.href : undefined);

    try {
      const res = await client.requestContact(
        {
          workflow_id: cfg.workflowId,
          first_name: data.first,
          last_name: data.last || undefined,
          email: data.email || undefined,
          phone: data.phone,
          force_first_channel: channel,
          source: "web-chat",
          // Context for the agent's first outreach: they were on the site and
          // explicitly asked to be reached on this channel.
          metadata: {
            ...(meta ?? {}),
            contact_request: true,
            requested_channel: channel,
            requested_at: new Date().toISOString(),
            context_note: `Website visitor asked to be contacted via ${channel}${pageUrl ? ` from ${pageUrl}` : ""}.`,
          },
        },
        { timeoutMs: cfg.requestTimeoutMs, maxRetries: 0 },
      );
      if (res?.lead?.id && !state.leadId) {
        state.leadId = res.lead.id;
        cfg.onLeadCaptured?.(res.lead.id);
      }
      // Re-assert the success copy for the channel actually submitted; drop the
      // header explainer (they've submitted — "leave your details" no longer
      // applies), keep the title, and swap to the success panel.
      c.successText.textContent = rc.successText[channel] ?? "";
      panel.headerSubtitle.hidden = true;
      c.view.setAttribute("data-state", "sent");
    } catch (err) {
      c.submitBtn.disabled = false;
      showContactError(rc.errorText);
      cfg.onError?.(err as Error);
    }
  }

  // ─── Contact validation feedback ────────────────────────────────────────

  function failContact(
    field: "first_name" | "last_name" | "email" | "phone",
    message: string,
  ): void {
    const c = panel.contact;
    if (!c) return;
    showContactError(message);
    const input = c.inputs[field];
    // For phone, outline the whole composite (flag + input), not just the input.
    const target = (input?.closest(".nexor-chat__phone") as HTMLElement | null) || input;
    target?.classList.add("nexor-chat__input--error");
    input?.focus();
  }
  function showContactError(message: string): void {
    const c = panel.contact;
    if (!c) return;
    c.error.textContent = message;
    c.error.hidden = false;
  }
  function clearContactError(): void {
    const c = panel.contact;
    if (!c) return;
    c.error.textContent = "";
    c.error.hidden = true;
    c.form
      .querySelectorAll(".nexor-chat__input--error")
      .forEach((el) => el.classList.remove("nexor-chat__input--error"));
  }

  // ─── Panel view ("capture" screen vs "chat") + "start over" reset ───────

  /** Swap the panel between the capture form, the chat, and the contact form. */
  function setView(view: "capture" | "chat" | "contact"): void {
    panel.el.setAttribute("data-view", view);
  }

  /** The normal screen for this widget — capture if still gating, else chat. */
  function baseView(): "capture" | "chat" {
    return state.captured ? "chat" : "capture";
  }

  /** Banner text for a restored conversation, personalised when we know who it is. */
  function identityText(): string {
    const l = state.pendingLead;
    const who = (l.first_name || l.email || l.phone || "").trim();
    return who ? cfg.resumeWithNameText.replace("{name}", who) : cfg.resumeText;
  }

  function renderResumeBanner(prepend = false): void {
    if (resumeBanner) return; // already showing
    const banner = buildResumeBanner(identityText(), cfg.startOverText);
    banner.resetBtn.addEventListener("click", startOver);
    // Restore path: body is empty, append → ends up on top, messages follow.
    // API-driven path: messages already exist, so insert at the very top.
    if (prepend && panel.body.firstChild) {
      panel.body.insertBefore(banner.el, panel.body.firstChild);
    } else {
      panel.body.appendChild(banner.el);
    }
    resumeBanner = banner.el;
  }

  /**
   * "Clear and start again" — start a fresh conversation EPISODE on the SAME
   * lead. We keep the visitor's identity (no re-capture, no duplicate lead) and
   * just clear the local transcript; the next turn tells the API to reset the
   * episode (deactivate the prior run/thread + clear the workflow's variables),
   * so the agent no longer brings up the previous conversation.
   */
  function startOver(): void {
    cancelGreeting();
    if (send.timer) {
      window.clearTimeout(send.timer);
      send.timer = 0;
    }
    if (send.splitTimer) {
      window.clearTimeout(send.splitTimer);
      send.splitTimer = 0;
    }
    send.pending = [];
    hideTyping();

    // Wipe the local transcript AND the persisted session (history + the known
    // lead), so nothing about the previous visitor survives the reset.
    try {
      window.localStorage.removeItem(historyKey);
    } catch {
      /* ignore */
    }
    state.history = [];
    state.shownOpening = null;
    resumeBanner = null;
    panel.body.replaceChildren();

    // Lead-capture widgets: "start again" is a clean slate → forget the known
    // identity and send the visitor BACK TO THE FORM. The next submit creates a
    // fresh lead; the first chat turn carries reset_episode so that if they
    // re-enter the same email, the server clears the old run/thread/variables
    // (no prior context bleeds in). A genuinely new email just makes a new lead.
    if (panel.captureView) {
      state.pendingLead = { ...(cfg.lead ?? {}) };
      state.captured = false;
      state.leadId = undefined;
      state.episodeChoiceOffered = false;
      state.resetEpisode = true;
      resetCaptureForm();
      setView("capture");
      syncComposerLock();
      const first = cfg.capture.fields[0];
      ((first && panel.captureInputs[first]) || panel.input).focus();
      return;
    }

    // No capture form (skip mode): keep the lead, reset the episode server-side,
    // and drop straight into a fresh greeting.
    state.resetEpisode = true;
    setView("chat");
    syncComposerLock();
    if (state.captured && canGreet()) scheduleGreeting();
    panel.input.focus();
  }

  /** Clear the capture form back to the configured lead (used by "start again"). */
  function resetCaptureForm(): void {
    const lead = state.pendingLead;
    if (panel.captureInputs.first_name) panel.captureInputs.first_name.value = lead.first_name ?? "";
    if (panel.captureInputs.last_name) panel.captureInputs.last_name.value = lead.last_name ?? "";
    if (panel.captureInputs.email) panel.captureInputs.email.value = lead.email ?? "";
    const def = resolveDefaultCountry(cfg.capture.phoneCountryCode);
    if (panel.phoneCountry) panel.phoneCountry.value = def.iso;
    if (panel.captureInputs.phone) {
      let national = "";
      if (lead.phone) {
        const digits = String(lead.phone).replace(/\D/g, "");
        const dialDigits = def.dial.replace(/\D/g, "");
        national = (digits.startsWith(dialDigits) ? digits.slice(dialDigits.length) : digits).slice(
          0,
          maxNsn(def),
        );
      }
      panel.captureInputs.phone.maxLength = maxNsn(def);
      panel.captureInputs.phone.value = national;
    }
    clearCaptureError();
  }

  // ─── Composer / send (debounced + coalesced) ────────────────────────────
  //
  // Visitors fire several short messages in a row (WhatsApp-style). We mirror
  // the WhatsApp contract — batch at the source, one LLM call per batch — by
  // buffering rapid messages in the browser and POSTing them as a SINGLE
  // combined turn after a short quiet window. The composer never locks, so the
  // user can keep typing and sending freely; messages that land mid-request are
  // sent in the next batch. (`const send` itself is declared near `state`
  // above so DOM-wiring callbacks don't hit a TDZ on it.)

  function onComposerSubmit(e: Event): void {
    e.preventDefault();
    const text = panel.input.value.trim();
    if (!text) return;
    panel.input.value = "";
    autosizeComposer(); // collapse the textarea back to one line
    queueMessage(text);
  }

  // Grow the composer textarea to fit its content. CSS max-height caps it at ~4
  // lines and switches on the scrollbar; box-sizing is border-box, so add the
  // 2px vertical border back so one line doesn't show a phantom scrollbar.
  function autosizeComposer(): void {
    const el = panel.input;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }

  function queueMessage(text: string): void {
    if (!state.captured) {
      const first = cfg.capture.fields[0];
      ((first && panel.captureInputs[first]) || panel.input).focus();
      return;
    }
    // Visitor engaged before the greeting fired — drop it. Showing a canned
    // greeting *after* their first real message would feel out of order.
    cancelGreeting();
    appendUser(text);
    send.pending.push(text);
    scheduleFlush();
  }

  /**
   * The opening message: the dashboard-authored `initial_message` (with
   * {{variables}} substituted from the lead we know) when present, else the
   * host-provided `greeting` fallback.
   */
  // Priority: host openingMessage override > dashboard initial_message > greeting.
  function greetingText(): string {
    const raw = cfg.openingMessage || state.serverInitialMessage;
    if (raw) return renderLeadTemplate(raw, state.pendingLead);
    return cfg.greeting ?? "";
  }

  /** Is there (or might there still be) an opening message worth showing? */
  function canGreet(): boolean {
    if (!state.webchatEnabled) return false;
    if (cfg.openingMessage) return true; // explicit override always greets
    // Config not back yet → it might still supply an opening, so allow it.
    if (!state.configResolved) return true;
    return !!state.serverInitialMessage || !!cfg.greeting;
  }

  function scheduleGreeting(): void {
    if (greeting.pending) return;
    greeting.pending = true;
    showTyping();
    greeting.timer = window.setTimeout(async () => {
      // An explicit openingMessage override wins regardless of server config, so
      // we can paint immediately. Otherwise wait for the dashboard config so we
      // show `initial_message` rather than the fallback (dots stay up meanwhile).
      if (!cfg.openingMessage) await configReady;
      if (!greeting.pending) return; // cancelled while waiting
      greeting.pending = false;
      greeting.timer = 0;
      hideTyping();
      const text = greetingText();
      if (text) {
        state.shownOpening = text; // tell the agent the exact opening shown
        appendBot(text);
      }
    }, GREETING_DELAY_MS);
  }

  function cancelGreeting(): void {
    if (!greeting.pending) return;
    if (greeting.timer) window.clearTimeout(greeting.timer);
    greeting.timer = 0;
    greeting.pending = false;
    hideTyping();
  }

  // ─── Server web-chat config (enabled + dashboard opening message) ────────

  async function loadServerConfig(): Promise<void> {
    try {
      const res = await client.getChatConfig(cfg.workflowId, {
        timeoutMs: 10_000,
        maxRetries: 1,
      });
      applyServerConfig(res.enabled !== false, res.initial_message ?? null);
    } catch {
      // Config fetch failed (offline, or an API without /chat/config): keep the
      // widget usable with the host-provided greeting and assume chat is on.
      applyServerConfig(true, null);
    }
  }

  function applyServerConfig(
    enabled: boolean,
    initialMessage: string | null,
  ): void {
    state.configResolved = true;
    state.serverInitialMessage = initialMessage;
    state.webchatEnabled = enabled;
    if (!enabled) {
      // Web chat is off for this workflow: pull the "chat" speed-dial option and,
      // if the panel is already open, swap to the unavailable notice.
      hideChatChannelButton();
      cancelGreeting();
      if (state.isOpen) showDisabled();
    }
  }

  /** Render the "chat unavailable" state inside the panel. */
  function showDisabled(): void {
    setView("chat");
    if (!state.disabledShown) {
      appendSystem(cfg.disabledText);
      state.disabledShown = true;
    }
    panel.input.disabled = true;
    panel.send.disabled = true;
    panel.input.placeholder = cfg.disabledText;
  }

  /** Drop just the "chat" button from the speed-dial fan (other channels stay). */
  function hideChatChannelButton(): void {
    for (const btn of launcher.channelButtons) {
      if (btn.key !== "chat") continue;
      const item = btn.el.closest(".nexor-chat__channel-item");
      if (item instanceof HTMLElement) item.style.display = "none";
    }
  }

  // Show the typing dots the INSTANT a message is queued. We still hold the
  // actual send for the quiet window, but the visitor gets immediate "I heard
  // you, I'm responding" feedback — the dots persist across the debounce + the
  // request, so there's never a dead 2.5s gap after they hit send.
  function showTyping(): void {
    if (send.typing) {
      // Already up — keep it pinned below the latest message.
      panel.body.appendChild(send.typing);
      scrollToBottom();
      return;
    }
    send.typing = appendNode(buildTypingIndicator());
  }

  function hideTyping(): void {
    send.typing?.remove();
    send.typing = null;
  }

  function scheduleFlush(): void {
    showTyping();
    if (send.timer) window.clearTimeout(send.timer);
    send.timer = window.setTimeout(() => void flush(), cfg.debounceMs);
  }

  // Mint + solve a fresh proof-of-work per turn. Single-use (anti-replay), so
  // never cache. PoW is the only anti-bot guard (Turnstile retired); retry once
  // on a transient failure before giving up.
  async function resolvePow(): Promise<PowSolution | undefined> {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const ch = await client.getPowChallenge(cfg.workflowId, {
          embedded: isEmbedded,
          options: { timeoutMs: 2_500, maxRetries: 0 },
        });
        const number = await solvePow(ch);
        if (number == null) return undefined;
        return {
          challenge: ch.challenge,
          salt: ch.salt,
          signature: ch.signature,
          number,
          expires: ch.expires,
        };
      } catch {
        if (attempt === 1) return undefined;
      }
    }
    return undefined;
  }

  async function flush(): Promise<void> {
    if (send.inFlight || send.pending.length === 0) return;
    const batch = send.pending.splice(0, send.pending.length);
    const message = batch.join("\n");
    send.inFlight = true;
    // If the lead is being pre-created from the capture form, let it settle so
    // the backend matches that lead by identity instead of racing it into a
    // duplicate. Best-effort: a failed/slow create never blocks the turn forever
    // (createLead has its own timeout) and the turn would create the lead anyway.
    if (state.leadCreating) {
      try {
        await state.leadCreating;
      } catch {
        /* ignore — the turn still creates/matches the lead */
      }
    }
    // Whether THIS turn resets the episode. Captured before the await so a
    // concurrent reset doesn't flip it mid-request.
    const turnResetsEpisode = state.resetEpisode;

    const lead = Object.keys(state.pendingLead).length ? state.pendingLead : undefined;
    // A chat turn is NOT idempotent (re-sending re-runs the LLM and re-inserts
    // the message). Never retry; just give it a generous timeout.
    const turnOptions = { timeoutMs: cfg.requestTimeoutMs, maxRetries: 0 };

    // Resolve proof-of-work just before sending (single-use, PoW-only guard).
    const pow = await resolvePow();

    try {
      const res = isEmbedded
        ? await client.widgetTurn(
            {
              workflow_id: cfg.workflowId,
              session_id: sessionId,
              message,
              client_prompt: cfg.clientPrompt,
              pow,
              // The opening the visitor actually saw, so the agent doesn't repeat
              // or contradict a per-page opening override.
              opening_shown: state.shownOpening ?? undefined,
              // First turn after "start over" → reset the conversation episode on
              // the same lead (clear prior run/thread/variables server-side).
              reset_episode: turnResetsEpisode || undefined,
              // The widget turn's lead schema is stricter than chat()'s: no metadata.
              lead: lead
                ? {
                    first_name: lead.first_name,
                    last_name: lead.last_name,
                    email: lead.email,
                    phone: lead.phone,
                  }
                : undefined,
              // Resolve metadata per-turn so a function form picks up live SPA state
              // (e.g. the current route) without re-mounting the widget.
              metadata: typeof cfg.metadata === "function" ? cfg.metadata() : cfg.metadata,
              parent_origin: parentOrigin,
            },
            turnOptions,
          )
        : await client.chat(
            {
              workflow_id: cfg.workflowId,
              session_id: sessionId,
              message,
              // Additive page/instance prompt injection (API appends to the
              // workflow persona). Per route, the host passes different values.
              system_prompt: cfg.systemPrompt,
              client_prompt: cfg.clientPrompt,
              opening_shown: state.shownOpening ?? undefined,
              reset_episode: turnResetsEpisode || undefined,
              pow,
              lead,
              metadata: typeof cfg.metadata === "function" ? cfg.metadata() : cfg.metadata,
            },
            turnOptions,
          );
      // Episode reset has landed; subsequent turns continue this fresh episode.
      if (turnResetsEpisode) state.resetEpisode = false;
      if (res.lead_id && !state.leadId) {
        state.leadId = res.lead_id;
        cfg.onLeadCaptured?.(state.leadId);
      }
      // Returning visitor recognised by the backend on a fresh local session →
      // offer "continue / start fresh" once (the API may have surfaced prior
      // context in this reply). With restored local history the resume banner
      // already covered this, so freshLocalSession gates it.
      if (
        res.matched_existing &&
        state.freshLocalSession &&
        !state.episodeChoiceOffered &&
        !turnResetsEpisode
      ) {
        state.episodeChoiceOffered = true;
        renderResumeBanner(true);
      }
      const reply = (res.reply ?? "").trim();
      const hasMedia = Array.isArray(res.media) && res.media.length > 0;
      hideTyping(); // clear the dots right before the reply lands
      if (reply) {
        // Media (if any) lands AFTER the last text bubble — pass it as the
        // split-reply completion so it never jumps ahead of a staggered reply.
        appendBotReply(reply, hasMedia ? () => appendBotMedia(res.media) : undefined);
      } else if (hasMedia) {
        // Media-only turn (the AI sent a file with no accompanying text) — render
        // the file, don't treat it as an empty/error reply.
        appendBotMedia(res.media);
      }
      // else: a successful turn with no text is an INTENTIONAL silent turn — the
      // agent is configured not to answer (e.g. the lead is in a lost/paused
      // status). Render nothing. The visitor simply gets no reply; we do NOT show
      // the error fallback (that's for real request failures, handled in catch).
    } catch (err) {
      hideTyping();
      // The workflow's web-chat channel was turned off server-side (403).
      // Surface the unavailable notice rather than a generic error.
      if (/web chat is not enabled/i.test((err as Error)?.message || "")) {
        applyServerConfig(false, state.serverInitialMessage);
      } else {
        appendSystem(cfg.errorText);
      }
      cfg.onError?.(err as Error);
    } finally {
      // Safety net — never leave dangling dots. But a split reply is mid-stagger
      // here (its first inter-bubble dots are already showing and its timer is
      // armed); clearing them would make the first gap flicker dot-less. Skip the
      // cleanup while the stagger owns the typing indicator.
      if (!send.splitTimer) hideTyping();
      send.inFlight = false;
      panel.input.focus();
      // Anything queued while the request was in flight goes out next.
      if (send.pending.length) scheduleFlush();
    }
  }

  // ─── Message rendering ──────────────────────────────────────────────────

  function appendUser(text: string): void {
    const ts = Date.now();
    appendBubble("user", text, ts);
    state.history.push({ role: "user", text, ts });
    saveHistory();
    cfg.onMessage?.({ role: "user", text });
  }

  // `showTime` is false for the non-final bubbles of a split reply: those arrive
  // in the same moment, so only the LAST bubble carries the timestamp. Persisted
  // the same way (ts omitted) so a reload renders identically.
  function appendBot(text: string, showTime = true): void {
    const ts = showTime ? Date.now() : undefined;
    appendBubble("bot", text, ts);
    state.history.push({ role: "bot", text, ts });
    saveHistory();
    if (!state.isOpen) bumpUnread();
    cfg.onMessage?.({ role: "bot", text });
  }

  // A bot bubble whose body is a File Library attachment. Each media item is its
  // own bubble (image inline / document as a link), persisted like any message
  // so a reload restores it. Skips items with no usable URL.
  function appendBotMedia(items: ChatMedia[] | undefined): void {
    if (!Array.isArray(items) || items.length === 0) return;
    for (const item of items) {
      if (!item || typeof item.url !== "string" || !item.url) continue;
      appendNode(buildMediaMessage(item));
      state.history.push({ role: "bot", text: "", ts: Date.now(), media: item });
    }
    saveHistory();
    if (!state.isOpen) bumpUnread();
  }

  // Render a bot reply, WhatsApp-style: split it on blank lines into separate
  // bubbles that arrive one after another with a short typing pause between.
  // `after` (if given) fires once the LAST bubble has landed — used to append
  // any media the reply carried so it never jumps ahead of a staggered reply.
  function appendBotReply(text: string, after?: () => void): void {
    const parts = cfg.splitReplies ? splitReply(text) : [text];
    if (parts.length <= 1) {
      appendBot(text);
      after?.();
      return;
    }
    const [first, ...rest] = parts;
    appendBot(first ?? text, false); // first bubble lands immediately, no timestamp
    let i = 0;
    const sendNext = (): void => {
      const part = rest[i];
      if (part === undefined) {
        after?.();
        return;
      }
      const isLast = i === rest.length - 1; // timestamp goes only on the last bubble
      i++;
      showTyping(); // dots between bubbles, like someone typing the next message
      // A human-feeling "typing" pause before this bubble appears: fixed if the
      // host set one, otherwise 3.0–5.0s scaled by how long THIS message is —
      // longer text reads as more time spent typing — plus a little jitter so the
      // cadence never feels mechanical.
      const delay = cfg.splitDelayMs > 0 ? cfg.splitDelayMs : typingDelayFor(part);
      send.splitTimer = window.setTimeout(() => {
        send.splitTimer = 0;
        hideTyping();
        appendBot(part, isLast);
        sendNext();
      }, delay);
    };
    sendNext();
  }

  /** Split on blank lines (one or more) → one bubble per paragraph block. */
  function splitReply(text: string): string[] {
    const parts = text
      .split(/\n[ \t]*\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length ? parts : [text];
  }

  /** "Typing" pause (ms) before a split bubble: 3.0–5.0s, scaled by length. */
  function typingDelayFor(text: string): number {
    const MIN = 3000;
    const MAX = 5000;
    const FULL_AT = 180; // message length (chars) at which the pause hits MAX
    const scaled = MIN + Math.min(text.length, FULL_AT) * ((MAX - MIN) / FULL_AT);
    const jitter = Math.random() * 300 - 150; // ±150ms so it never feels mechanical
    return Math.round(Math.max(MIN, Math.min(MAX, scaled + jitter)));
  }

  // ─── Transcript persistence (survives reloads up to SESSION_RETENTION_MS) ──

  function saveHistory(): void {
    try {
      window.localStorage.setItem(
        historyKey,
        // Persist the known lead alongside the transcript so a returning visitor
        // is recognised (and not re-asked for their details) after a reload.
        JSON.stringify({
          ts: Date.now(),
          history: state.history,
          lead: state.pendingLead,
        }),
      );
      touchSession(); // keep the session alive in step with the transcript
    } catch {
      /* storage unavailable — in-memory history still works for this load. */
    }
  }

  function loadPersisted(): {
    history: Array<{ role: "user" | "bot"; text: string; ts?: number; media?: ChatMedia }>;
    lead: ChatInitOptions["lead"] | undefined;
  } {
    const empty = { history: [], lead: undefined };
    try {
      const raw = window.localStorage.getItem(historyKey);
      if (!raw) return empty;
      const o = JSON.parse(raw);
      if (!o || !Array.isArray(o.history)) return empty;
      if (Date.now() - (o.ts || 0) > SESSION_RETENTION_MS) {
        window.localStorage.removeItem(historyKey);
        return empty;
      }
      return {
        history: o.history,
        lead: o.lead && typeof o.lead === "object" ? o.lead : undefined,
      };
    } catch {
      return empty;
    }
  }

  function appendSystem(text: string): void {
    appendBubble("system", text);
  }

  function appendBubble(
    role: MessageRole,
    text: string,
    ts?: number,
  ): HTMLDivElement {
    return appendNode(buildMessage(role, text, ts, cfg.locale));
  }

  // Keep relative timestamps ("hace 3 minutos") fresh while the panel is open.
  const refreshTimer = window.setInterval(() => {
    panel.body
      .querySelectorAll<HTMLTimeElement>(".nexor-chat__msg-time[data-ts]")
      .forEach((el) => {
        const ts = Number(el.dataset.ts);
        if (ts) el.textContent = formatRelativeTime(ts, cfg.locale);
      });
  }, 60_000);

  function appendNode<T extends HTMLElement>(node: T): T {
    panel.body.appendChild(node);
    // Keep the typing indicator pinned to the bottom: if the visitor fires
    // another message while we're awaiting a reply, the dots must stay BELOW
    // it (the bot hasn't "typed" yet), not stranded between messages.
    if (send.typing && send.typing !== node) panel.body.appendChild(send.typing);
    scrollToBottom();
    return node;
  }

  function scrollToBottom(): void {
    panel.body.scrollTop = panel.body.scrollHeight;
  }

  // ─── Unread badge ───────────────────────────────────────────────────────

  function bumpUnread(): void {
    state.unread++;
    launcher.badge.textContent = String(state.unread);
    launcher.badge.style.display = "flex";
  }
  function clearUnread(): void {
    state.unread = 0;
    launcher.badge.style.display = "none";
  }

  // ─── Composer lock (used while capture form is visible) ─────────────────

  function syncComposerLock(): void {
    const lock = !state.captured;
    panel.input.disabled = lock;
    panel.send.disabled = lock;
    panel.input.placeholder = lock
      ? "Complete the form above to start…"
      : cfg.inputPlaceholder;
  }

  // ─── Public handle ──────────────────────────────────────────────────────

  return {
    open,
    close,
    toggle,
    // Programmatic send goes through the same debounce/coalesce path. Resolves
    // immediately — the message is queued, not necessarily sent yet.
    send: (text: string) => {
      queueMessage(text);
      return Promise.resolve();
    },
    destroy: () => {
      if (send.timer) window.clearTimeout(send.timer);
      if (send.splitTimer) window.clearTimeout(send.splitTimer);
      if (greeting.timer) window.clearTimeout(greeting.timer);
      window.clearInterval(refreshTimer);
      if (onDocPointerDown) document.removeEventListener("pointerdown", onDocPointerDown);
      root.remove();
    },
    getSessionId: () => sessionId,
  };
}

// ─── Module-scoped helpers ────────────────────────────────────────────────

function ensureBrowser(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    throw new Error("Nexor SDK: initChat() requires a browser environment.");
  }
}

function buildRoot(cfg: ResolvedCfg): HTMLDivElement {
  const root = document.createElement("div");
  root.className = "nexor-chat";
  root.setAttribute("data-position", cfg.position);
  root.setAttribute("data-open", "false");
  return root;
}

function readCaptureFields(panel: PanelRefs): Record<string, string> {
  const out: Record<string, string> = {};
  const inputs = panel.captureInputs;
  if (inputs.first_name) out.first_name = inputs.first_name.value.trim();
  if (inputs.last_name) out.last_name = inputs.last_name.value.trim();
  if (inputs.email) out.email = inputs.email.value.trim();
  // Phone holds national digits only; onCaptureSubmit prepends the country dial.
  if (inputs.phone) out.phone = inputs.phone.value.replace(/\D/g, "");
  return out;
}

// Used by initChat's launcher → keeps the ref objects in scope for TS.
type _Refs = LauncherRefs & PanelRefs;
void (null as unknown as _Refs);
